"use client";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, Download, ArrowUpRight, ArrowDownLeft, Clock, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { AdvancedFilterModal } from "@/components/modals/advanced-filter-modal";

export default function TransactionsPage() {
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Transactions</h1>
          <p className="text-[11px] text-muted-foreground uppercase font-bold tracking-widest mt-1">Real-time payment & money flow</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-9 text-xs font-bold px-4">
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
          <AdvancedFilterModal>
            <Button size="sm" className="h-9 text-xs font-bold px-4 bg-primary text-primary-foreground hover:bg-primary/90">
              <Filter className="mr-2 h-4 w-4" /> Advanced Filter
            </Button>
          </AdvancedFilterModal>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="p-4 border shadow-sm flex flex-col justify-between hover:border-emerald-500/30 transition-colors">
          <div className="flex justify-between items-start">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total Inflow</p>
            <div className="h-7 w-7 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-600" />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-bold text-emerald-600">₦120.2M</h3>
            <p className="text-[10px] font-bold text-muted-foreground mt-1">12,450 successful receipts</p>
          </div>
        </Card>
        <Card className="p-4 border shadow-sm flex flex-col justify-between hover:border-blue-500/30 transition-colors">
          <div className="flex justify-between items-start">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total Outflow</p>
            <div className="h-7 w-7 rounded-full bg-blue-500/10 flex items-center justify-center">
              <ArrowUpRight className="h-3.5 w-3.5 text-blue-600" />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-bold text-blue-600">₦30.2M</h3>
            <p className="text-[10px] font-bold text-muted-foreground mt-1">452 payouts & settlements</p>
          </div>
        </Card>
        <Card className="p-4 border shadow-sm flex flex-col justify-between hover:border-amber-500/30 transition-colors">
          <div className="flex justify-between items-start">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Pending Flow</p>
            <div className="h-7 w-7 rounded-full bg-amber-500/10 flex items-center justify-center">
              <Clock className="h-3.5 w-3.5 text-amber-600" />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-bold text-amber-600">₦4.2M</h3>
            <p className="text-[10px] font-bold text-muted-foreground mt-1">Processing transactions</p>
          </div>
        </Card>
        <Card className="p-4 border shadow-sm flex flex-col justify-between hover:border-primary/30 transition-colors">
          <div className="flex justify-between items-start">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total Volume</p>
            <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
              <Activity className="h-3.5 w-3.5 text-primary" />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-bold">₦150.4M</h3>
            <p className="text-[10px] font-bold text-muted-foreground mt-1">All processed transactions</p>
          </div>
        </Card>
      </div>

      <Card className="border shadow-sm overflow-hidden">
        <div className="p-3 border-b bg-muted/20 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="relative w-full max-w-sm flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search ref, email or amount..."
              className="pl-9 h-10 text-sm bg-background border-none focus-visible:ring-1 focus-visible:ring-primary/20 w-full"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0 no-scrollbar">
            <span className="text-[10px] font-bold text-muted-foreground uppercase whitespace-nowrap hidden sm:inline-block">Timeframe:</span>
            <Button variant="ghost" size="sm" className="h-8 text-[11px] font-bold px-3 shrink-0">Today</Button>
            <Button variant="ghost" size="sm" className="h-8 text-[11px] font-bold px-3 shrink-0">7 Days</Button>
            <Button variant="ghost" size="sm" className="h-8 text-[11px] font-bold px-3 shrink-0 text-primary bg-primary/5">30 Days</Button>
          </div>
        </div>
        
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-9 text-[10px] font-bold uppercase tracking-wider">Amount</TableHead>
              <TableHead className="h-9 text-[10px] font-bold uppercase tracking-wider">Customer / Recipient</TableHead>
              <TableHead className="h-9 text-[10px] font-bold uppercase tracking-wider">Channel</TableHead>
              <TableHead className="h-9 text-[10px] font-bold uppercase tracking-wider">Reference</TableHead>
              <TableHead className="h-9 text-[10px] font-bold uppercase tracking-wider">Status</TableHead>
              <TableHead className="h-9 text-[10px] font-bold uppercase tracking-wider text-right">Timestamp</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[
              { amount: "₦15,000.00", customer: "isaac@example.com", channel: "Mastercard", ref: "TX_827364", status: "Success", type: "in", date: "2m ago" },
              { amount: "₦2,500.00", customer: "Virtual Account (WEMA)", channel: "Transfer", ref: "VA_928374", status: "Success", type: "in", date: "15m ago" },
              { amount: "₦12,500.00", customer: "Vendor Payout: Aliko Ent.", channel: "Payout", ref: "PO_112233", status: "Settled", type: "out", date: "1h ago" },
              { amount: "₦45,000.00", customer: "john@doe.com", channel: "USSD", ref: "TX_009988", status: "Failed", type: "fail", date: "3h ago" },
              { amount: "₦8,500.00", customer: "jane@company.com", channel: "Visa", ref: "TX_445566", status: "Pending", type: "wait", date: "5h ago" },
            ].map((tx, i) => (
              <TableRow key={i} className="hover:bg-muted/5 group">
                <TableCell className="py-2.5">
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "h-6 w-6 rounded flex items-center justify-center",
                      tx.type === "in" ? "bg-emerald-500/10 text-emerald-600" : 
                      tx.type === "out" ? "bg-blue-500/10 text-blue-600" :
                      tx.type === "fail" ? "bg-destructive/10 text-destructive" : "bg-amber-500/10 text-amber-600"
                    )}>
                      {tx.type === "in" ? <ArrowDownLeft className="h-3 w-3" /> : 
                       tx.type === "out" ? <ArrowUpRight className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                    </div>
                    <span className="text-xs font-bold tracking-tight">{tx.amount}</span>
                  </div>
                </TableCell>
                <TableCell className="py-2.5">
                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-foreground">{tx.customer}</span>
                    <span className="text-[9px] text-muted-foreground font-medium uppercase tracking-tighter">Verified Channel</span>
                  </div>
                </TableCell>
                <TableCell className="py-2.5">
                  <Badge variant="outline" className="text-[9px] font-bold py-0 h-4 border-muted-foreground/20">{tx.channel}</Badge>
                </TableCell>
                <TableCell className="py-2.5 font-mono text-[10px] text-muted-foreground uppercase">{tx.ref}</TableCell>
                <TableCell className="py-2.5">
                  <div className="flex items-center gap-1.5">
                    <div className={cn(
                      "h-1.5 w-1.5 rounded-full",
                      tx.status === "Success" || tx.status === "Settled" ? "bg-emerald-500" :
                      tx.status === "Pending" ? "bg-amber-500" : "bg-destructive"
                    )} />
                    <span className="text-[10px] font-bold uppercase">{tx.status}</span>
                  </div>
                </TableCell>
                <TableCell className="py-2.5 text-right text-[10px] font-bold text-muted-foreground uppercase">{tx.date}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        <div className="p-3 border-t bg-muted/10 flex items-center justify-between">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Page 1 of 42 (1,234 items)</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled><ArrowDownLeft className="h-3 w-3 rotate-45" /></Button>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0"><ArrowUpRight className="h-3 w-3 rotate-45" /></Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
